import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import SortableHeader from '../../components/SortableHeader';
import { SearchIcon, EditIcon, TrashIcon, CopyIcon } from '../../components/Icons';

const FilterSelect = ({ label, name, value, onChange, options, placeholder }) => (
  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
    <select
      name={name}
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '0.45rem 0.65rem',
        border: '1px solid var(--border-light)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--bg-card)',
        color: 'var(--text-primary)',
        fontSize: '0.8125rem'
      }}
    >
      <option value="">{placeholder || `All ${label}`}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-secondary)'
};

const createEmptyProviderForm = () => ({
  name: '', code: '', category: '', sub_category: '', is_active: true, isFreeCredit: false
});

const Providers = () => {
  const { hasPermission } = useAuth();
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ category: '', sub_category: '' });
  const [sort, setSort] = useState({ sortBy: 'name', sortOrder: 'asc' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(createEmptyProviderForm);

  const fetchDropdowns = useCallback(async () => {
    const [categoriesRes, subCatsRes] = await Promise.all([
      gameService.getCategories({ limit: 1000 }),
      gameService.getSubCategories({ limit: 1000 })
    ]);
    if (categoriesRes.success) setCategories(categoriesRes.data);
    if (subCatsRes.success) setSubCategories(subCatsRes.data);
  }, []);

  useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);

  const fetchProviders = useCallback(async (page = pagination.page, search = searchTerm, activeFilters = filters, limit = pagination.limit, activeSort = sort, clearList = false) => {
    try {
      setLoading(true);
      if (clearList) setProviders([]);
      const params = { page, limit, search, sortBy: activeSort.sortBy, sortOrder: activeSort.sortOrder, ...activeFilters };
      // Strip empty filter values
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const res = await gameService.getProviders(params);
      if (res.success) {
        setProviders(res.data);
        setPagination(prev => ({ ...prev, page: res.pagination.page, total: res.pagination.total, pages: res.pagination.pages }));
      }
    } catch (err) {
      setError('An error occurred while fetching providers');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm, filters, sort]);

  useEffect(() => {
    const t = setTimeout(() => fetchProviders(1, searchTerm, filters, pagination.limit, sort), 400);
    return () => clearTimeout(t);
  }, [searchTerm, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const canCreate = hasPermission('providers', 'create');
  const canEdit = hasPermission('providers', 'edit');
  const canDelete = hasPermission('providers', 'delete');
  const showActions = canCreate || canEdit || canDelete;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSortChange = (sortBy, sortOrder) => {
    const nextSort = { sortBy, sortOrder };
    if (sort.sortBy === nextSort.sortBy && sort.sortOrder === nextSort.sortOrder) return;

    setSort(nextSort);
    fetchProviders(1, searchTerm, filters, pagination.limit, nextSort, true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const newData = { ...prev, [name]: val };
      if (name === 'name' && !editId) {
        newData.code = value.replace(/\s+/g, '').toUpperCase();
      }
      return newData;
    });
  };

  const openAddModal = () => { setEditId(null); setFormData(createEmptyProviderForm()); setIsModalOpen(true); };
  const openCloneModal = (p) => {
    setEditId(null);
    setFormData({
      name: `${p.name} Copy`,
      code: `${p.code}_COPY`,
      category: p.category?._id || '',
      sub_category: p.sub_category?._id || '',
      is_active: p.is_active,
      isFreeCredit: p.isFreeCredit === true
    });
    setIsModalOpen(true);
  };
  const openEditModal = (p) => {
    setEditId(p._id);
    setFormData({ 
      name: p.name, 
      code: p.code || '', 
      category: p.category?._id || '', 
      sub_category: p.sub_category?._id || '', 
      is_active: p.is_active,
      isFreeCredit: p.isFreeCredit === true
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the provider "${name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        const res = await gameService.deleteProvider(id);
        if (res.success) {
          fetchProviders();
        } else {
          alert(res.message || 'Failed to delete provider');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'An error occurred while deleting provider';
        alert(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = { ...formData, sub_category: formData.sub_category || null };
      const response = editId ? await gameService.updateProvider(editId, payload) : await gameService.createProvider(payload);
      if (response.success) { setIsModalOpen(false); fetchProviders(); }
      else alert(response.message || 'Failed to save provider');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'An error occurred while saving provider';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="providers-page">
      {canCreate && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={openAddModal}>Add New Provider</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container" style={{ marginBottom: '0.75rem' }}>
          <div className="search-icon-wrapper"><SearchIcon /></div>
          <input type="text" className="search-input" placeholder="Search provider name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <FilterSelect label="Category" name="category" value={filters.category} onChange={handleFilterChange}
            options={categories.map(c => ({ value: c._id, label: c.name }))} />
          <FilterSelect label="Sub Category" name="sub_category" value={filters.sub_category} onChange={handleFilterChange}
            options={subCategories.map(s => ({ value: s._id, label: s.name }))} />
        </div>
      </div>

      {loading && providers.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading providers...</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <SortableHeader label="Code" sortKey="code" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                <SortableHeader label="Name" sortKey="name" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                <th style={tableHeaderStyle}>Category</th>
                <th style={tableHeaderStyle}>Sub Category</th>
                <th style={tableHeaderStyle}>Free Credit</th>
                <th style={tableHeaderStyle}>Status</th>
                {showActions && <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {providers.length === 0 ? (
                 <tr><td colSpan={showActions ? 7 : 6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No providers found.</td></tr>
              ) : providers.map((p) => (
                <tr key={p._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{p.code}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.category?.name || '-'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.sub_category?.name || '-'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{p.isFreeCredit ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, backgroundColor: p.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: p.is_active ? 'var(--success)' : 'var(--error)' }}>{p.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                   {showActions && (
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        {canCreate && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openCloneModal(p)}
                          >
                            <CopyIcon size={14} />
                            Clone
                          </button>
                        )}
                        {canEdit && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openEditModal(p)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => handleDelete(p._id, p.name)}
                          >
                            <TrashIcon size={14} />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                   )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          <Pagination pagination={pagination} onPageChange={(pg) => fetchProviders(pg, searchTerm, filters, pagination.limit, sort)} onLimitChange={(l) => {
            setPagination(prev => ({ ...prev, limit: l }));
            fetchProviders(1, searchTerm, filters, l, sort);
          }} loading={loading} itemName="providers" />
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editId ? 'Edit Provider' : 'Add New Provider'}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Provider Name *</label>
              <input type="text" name="name" className="input" value={formData.name} onChange={handleInputChange} required placeholder="e.g. Joker" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Provider Code *</label>
              <input type="text" name="code" className="input" value={formData.code} onChange={handleInputChange} required placeholder="e.g. JOKER" style={{ textTransform: 'uppercase' }} />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Category *</label>
            <select name="category" className="input" value={formData.category} onChange={handleInputChange} required>
              <option value="">Select Category</option>
              {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Sub Category</label>
            <select name="sub_category" className="input" value={formData.sub_category} onChange={handleInputChange}>
              <option value="">None</option>
              {subCategories.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</span>
            <label className="switch">
              <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} />
              <span className="slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Free Credit</span>
            <label className="switch">
              <input type="checkbox" name="isFreeCredit" aria-label="Free Credit" checked={formData.isFreeCredit} onChange={handleInputChange} />
              <span className="slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn" onClick={() => setIsModalOpen(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : (editId ? 'Update Provider' : 'Create Provider')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Providers;
